export const POLL_MS = 2000;
export const POLL_TIMEOUT_MS = 90000;
export const KEEPALIVE_MS = 30000;
export const CONNECT_TIMEOUT_MS = 30000;

const TERMINAL = new Set(["ended", "error", "deviceoffline"]);

const STATUS_TEXT = {
  requested: "Requesting…",
  starting: "Starting…",
  mediaconnected: "Media connected…",
  stopping: "Stopping…",
  live: "LIVE",
  ended: "Ended",
  error: "Error",
  deviceoffline: "Vehicle offline",
};

export function createNexusLiveSession({
  transport,
  video,
  onState,
  keepalive = true,
  endedOnDisconnect = false,
}) {
  let active = false;
  let sessionToken = null;
  let pc = null;
  let ws = null;
  let pollTimer = null;
  let keepaliveTimer = null;
  let connectTimer = null;
  let startedAt = 0;
  let wasLive = false;

  const setState = (state, detail) => onState?.(state, detail);

  function cleanup() {
    active = false;
    clearInterval(pollTimer);
    clearInterval(keepaliveTimer);
    clearTimeout(connectTimer);
    pollTimer = null;
    keepaliveTimer = null;
    connectTimer = null;

    try {
      ws?.close();
    } catch {}
    try {
      pc?.close();
    } catch {}
    ws = null;
    pc = null;

    if (video && video.srcObject) {
      video.srcObject.getTracks().forEach((t) => t.stop());
      video.srcObject = null;
    }
  }

  function fail(text) {
    const token = sessionToken;
    cleanup();
    sessionToken = null;
    setState("error", text);
    if (token) transport.stop(token).catch(() => {});
  }

  function startKeepalive() {
    if (!keepalive || keepaliveTimer) return;
    transport.keepalive(sessionToken).catch(() => {});
    keepaliveTimer = setInterval(() => {
      if (sessionToken) transport.keepalive(sessionToken).catch(() => {});
    }, KEEPALIVE_MS);
  }

  // A dropped connection during playback is usually the file finishing, not a failure —
  // the poll stopped at "live", so ask the server once before declaring an error.
  function lostConnection(text) {
    if (!endedOnDisconnect || !wasLive || !sessionToken) {
      fail(text);
      return;
    }
    const token = sessionToken;
    transport
      .status(token)
      .then((body) => {
        if (!active) return;
        if (body.status === "ended") {
          cleanup();
          sessionToken = null;
          setState("idle", STATUS_TEXT.ended);
          transport.stop(token).catch(() => {});
        } else fail(text);
      })
      .catch(() => {
        if (active) fail(text);
      });
  }

  function connectWebRtc(webRtc) {
    connectTimer = setTimeout(
      () => lostConnection("Connection timeout"),
      CONNECT_TIMEOUT_MS,
    );

    pc = new RTCPeerConnection({
      iceServers: webRtc.iceServers.map((s) => ({
        urls: s.urls,
        username: s.username || undefined,
        credential: s.credential || undefined,
      })),
    });

    const thisPc = pc;
    pc.addTransceiver("video", { direction: "recvonly" });

    pc.ontrack = (e) => {
      if (!video) return;
      video.srcObject = e.streams[0] || new MediaStream([e.track]);
      video.play?.()?.catch(() => {});
    };

    pc.onconnectionstatechange = () => {
      if (pc !== thisPc) return;
      if (pc.connectionState === "connected") {
        clearTimeout(connectTimer);
        connectTimer = null;
        wasLive = true;
        setState("live", STATUS_TEXT.live);
      } else if (pc.connectionState === "failed")
        lostConnection("WebRTC failed");
      else if (pc.connectionState === "disconnected") {
        clearTimeout(connectTimer);
        connectTimer = setTimeout(
          () => lostConnection("Connection timeout"),
          CONNECT_TIMEOUT_MS,
        );
        setState("connecting", "Reconnecting…");
      }
    };

    ws = new WebSocket(webRtc.signalingWssUrl);
    const thisWs = ws;

    const send = (action, payload) => {
      if (thisWs.readyState === 1) {
        thisWs.send(
          JSON.stringify({
            action,
            messagePayload: btoa(JSON.stringify(payload)),
          }),
        );
      }
    };

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        send("ICE_CANDIDATE", {
          candidate: e.candidate.candidate,
          sdpMid: e.candidate.sdpMid,
          sdpMLineIndex: e.candidate.sdpMLineIndex,
        });
      }
    };

    thisWs.onopen = async () => {
      try {
        const offer = await thisPc.createOffer();
        await thisPc.setLocalDescription(offer);
        send("SDP_OFFER", { type: offer.type, sdp: offer.sdp });
      } catch (err) {
        console.error("nexus live: offer error", err);
      }
    };

    let answered = false;
    const early = [];

    let signaling = Promise.resolve();

    const apply = async (type, payload) => {
      if (type === "SDP_ANSWER") {
        try {
          await thisPc.setRemoteDescription(payload);
        } catch (err) {
          console.error("nexus live: answer rejected", err);
          if (active && pc === thisPc) fail("WebRTC failed");
          return;
        }
        answered = true;
        while (early.length > 0) await thisPc.addIceCandidate(early.shift());
      } else if (type === "ICE_CANDIDATE") {
        if (answered) await thisPc.addIceCandidate(payload);
        else early.push(payload);
      }
    };

    thisWs.onmessage = (ev) => {
      if (!ev.data) return;
      let msg;
      let payload;
      try {
        msg = JSON.parse(ev.data);
        if (!msg.messagePayload) return;
        payload = JSON.parse(atob(msg.messagePayload));
      } catch {
        return;
      }
      signaling = signaling
        .then(() => apply(msg.messageType, payload))
        .catch((err) => console.error("nexus live: signaling error", err));
    };

    thisWs.onerror = () => {
      if (ws !== thisWs || !active) return;
      // Once media is up the signaling socket is dispensable — a late socket error
      // must not paint a "connecting" overlay over a healthy stream.
      if (thisPc.connectionState !== "connected")
        setState("connecting", "Signaling error");
    };

    thisWs.onclose = () => {
      if (ws !== thisWs || !active) return;
      if (thisPc.connectionState === "connected") return;
      lostConnection("Signaling closed");
    };
  }

  async function poll() {
    if (!active || !sessionToken) return;

    if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
      fail("Timeout");
      return;
    }

    let body;
    try {
      body = await transport.status(sessionToken);
    } catch {
      return;
    }
    if (!active) return;

    if (TERMINAL.has(body.status)) {
      const text = STATUS_TEXT[body.status] || body.status;
      cleanup();
      sessionToken = null;
      setState(body.status === "ended" ? "idle" : "error", text);
      return;
    }

    if (body.status === "live" && body.webRtc && !pc) {
      clearInterval(pollTimer);
      pollTimer = null;
      startKeepalive();
      setState("connecting", "Connecting…");
      connectWebRtc(body.webRtc);
    } else if (!pc) {
      const text =
        body.status === "live"
          ? STATUS_TEXT.starting
          : STATUS_TEXT[body.status] || body.status;
      setState("connecting", text);
    }
  }

  async function start() {
    if (active) return;
    active = true;
    setState("connecting", STATUS_TEXT.requested);

    let started;
    try {
      started = await transport.start();
    } catch {
      // A stop (or a restart, which stops first) can land while the start mutation is in
      // flight. This session is finished either way, but only a session that is still the
      // live one may paint the error: otherwise a stale rejection overwrites the fresh
      // session's "Requesting…" with an error overlay for a stream that is starting fine.
      const stillMine = active;
      cleanup();
      if (stillMine) setState("error", "Could not start stream");
      return;
    }

    if (!active) {
      transport.stop(started.sessionToken).catch(() => {});
      return;
    }

    if (TERMINAL.has(started.status)) {
      cleanup();
      setState("error", STATUS_TEXT[started.status] || started.status);
      return;
    }

    sessionToken = started.sessionToken;
    startedAt = Date.now();
    setState("connecting", STATUS_TEXT[started.status] || started.status);

    pollTimer = setInterval(poll, POLL_MS);
  }

  function stop() {
    const token = sessionToken;
    cleanup();
    sessionToken = null;
    setState("idle", "Ready");
    if (token) transport.stop(token).catch(() => {});
  }

  return {
    start,
    stop,
    __peerConnection: () => pc,
  };
}
